import { useTranslation } from "react-i18next";
import Table from "react-bootstrap/Table";
import Pagination from "../Pagination/Pagination";
import Filter from "../Filter/Filter";
import Badge from "react-bootstrap/Badge";
import { COLUMN_TYPE } from "../../constants";
import {
  getDateAsIsoString,
  getTextWithCapitalizedFirstLetter,
} from "../../helpers/textHelper";
import "./List.scss";
import { Option } from "../../generalTypes";
import { SortProperty } from "../SortingSelector/SortingSelector";

export interface Column {
  propertyName: string;
  headerName?: string;
  type: string;
  optional?: boolean;
  hidden?: boolean;
  sortable?: boolean;
  wide?: boolean;
}

export interface FilterInput {
  type: string;
  propertyName: string;
  propertyLabel: string;
  options?: Array<Option>;
  value: string;
  onChange: Function;
}

interface ListProps {
  columns: Array<Column>;
  items: Array<any>;
  currentPage?: number;
  pageSize?: number;
  totalNumberOfItems?: number;
  onPageChange?: Function;
  onPageSizeChange?: Function;
  currentSort?: SortProperty;
  onSortChange?: Function;
  filterInputs?: Array<FilterInput>;
  onFilterChange?: Function;
}

const getStatusStyleClass = (status: string) => {
  switch (status) {
    case 'submitted':
      return 'bg-light text-dark';
    case 'processing':
      return 'bg-info text-white';
    case 'failed':
      return 'bg-danger text-white';
    case 'processed':
    case 'successful':
      return 'bg-success text-white';
    default:
      return 'bg-white text-dark';
  }
}

const getCells = (item: any, columns: Array<Column>, t: any) => {
  let cells = [];
  for (let column of columns) {
    let cellValue = item[column.propertyName];
    let cellClassName = "p-114-rem text-content-black text-break";

    if (column.type === COLUMN_TYPE.TEXT_TRANSLATABLE) {
      cellValue = t(getTextWithCapitalizedFirstLetter(cellValue));
    }
    if (column.type === COLUMN_TYPE.DATE) {
      cellValue = getDateAsIsoString(cellValue);
    }
    if (column.type === COLUMN_TYPE.COMPONENT) {
      cellClassName = "p-2 text-end";
    }
    if (column.type === COLUMN_TYPE.STATUS) {
      cellValue = (
        <Badge pill bg="" className={getStatusStyleClass(cellValue.toLowerCase())}>
          {t(getTextWithCapitalizedFirstLetter(cellValue))}
        </Badge>
      );
    }

    if (column.wide) {
      cellClassName += " w-50";
    }

    if (column.hidden) {
      cellClassName += " d-none";
    } else if (column.optional) {
      cellClassName += " d-none d-lg-table-cell";
    }

    cells.push(
      <td className={cellClassName} key={cells.length}>
        {cellValue}
      </td>
    );
  }
  return cells;
};

export default function List(props: ListProps) {
  const { t } = useTranslation();

  return (
    <>
      <Filter
        columns={props.columns}
        currentSort={
          props.currentSort
            ? props.currentSort
            : {
                propertyName: "",
                type: "",
              }
        }
        onSortChange={(sortField: string, sortOrder: string) => {
          if (props.onSortChange) {
            props.onSortChange(sortField, sortOrder);
          }
        }}
        filterInputs={props.filterInputs ? props.filterInputs : []}
        onFilterChange={(action: string) => {
          if (props.onFilterChange) {
            props.onFilterChange(action);
          }
        }}
      />
      <Table
        hover
        className={
          "bg-white" +
          (props.totalNumberOfItems != null && props.totalNumberOfItems > 0
            ? " mb-0"
            : " mb-5")
        }
      >
        <thead>
          <tr className="table-header-row">
            {props.columns.map((column, i) => (
              <th
                className={
                  "ps-114-rem py-086-rem font-weight-600" +
                  (column.hidden ? " d-none" : "") +
                  (!column.hidden && column.optional
                    ? " d-none d-lg-table-cell"
                    : "") +
                  (column.wide ? " w-50" : "")
                }
                key={i}
              >
                {column.headerName ? t(column.headerName) : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.items.map((item, i) => (
            <tr key={i}>{getCells(item, props.columns, t)}</tr>
          ))}
          {props.items.length === 0 && (
            <tr>
              <td
                className="p-114-rem text-center text-light-gray"
                colSpan={props.columns.length}
              >
                <i>{t("No results")}</i>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {props.onPageSizeChange != null &&
        props.onPageChange != null &&
        props.currentPage != null &&
        props.pageSize != null &&
        props.totalNumberOfItems != null &&
        props.totalNumberOfItems > 0 && (
          <Pagination
            onPageSizeChange={props.onPageSizeChange}
            onPageChange={props.onPageChange}
            currentPage={props.currentPage}
            pageSize={props.pageSize}
            totalCount={props.totalNumberOfItems}
          />
        )}
    </>
  );
}
